import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { OUTCOME_META } from "@/lib/meta";
import { cn } from "@/lib/utils";

export default function RecentExecutions() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/executions/recent", { params: { limit: 8 } })
      .then(({ data }) => setItems(data.executions))
      .catch((e) => {
        console.warn("Failed to load recent executions:", formatApiError(e));
        setError(true);
      });
  }, []);

  if (error) return null;

  const renderBody = () => {
    if (items === null) {
      return (
        <div className="space-y-2">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <p data-testid="recent-executions-empty" className="text-sm text-muted-foreground">
          No outcomes recorded yet. Open a runbook and log the result of a run.
        </p>
      );
    }
    return (
      <ul className="divide-y divide-border">
        {items.map((ex) => {
          const m = OUTCOME_META[ex.status] || OUTCOME_META.success;
          return (
            <li key={ex.id} data-testid={`recent-execution-${ex.id}`} className="flex items-start justify-between gap-4 py-2.5 text-sm">
              <div className="min-w-0 space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={cn("border text-xs", m.badge)}>{m.label}</Badge>
                  <Link to={`/runbooks/${ex.runbook_id}`} className="font-medium hover:text-primary truncate">
                    {ex.runbook_title}
                  </Link>
                  <span className="text-muted-foreground text-xs">by {ex.user_name}</span>
                </div>
                {ex.notes && <p className="text-muted-foreground text-xs truncate">{ex.notes}</p>}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(ex.timestamp).toLocaleString()}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section data-testid="recent-executions" className="rounded-xl border border-border bg-card p-5 space-y-3">
      <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
        <Activity className="h-5 w-5 text-primary" /> Recent executions
      </h2>
      {renderBody()}
    </section>
  );
}
